/**
 * Cascading Select Editor
 * 
 * A reusable utility for creating cascading (dependent) select dropdowns where
 * child select options depend on parent selections.
 * 
 * USAGE EXAMPLE:
 * 
 * ```javascript
 * import { renderCascadingSelectEditor } from './cascadingSelectEditor.js';
 * 
 * // Define schema for cascading selects
 * const schema = {
 *   "myReader": {
 *     "server": {
 *       label: "Server",
 *       getter: async () => {
 *         // Fetch server list from API
 *         return [{url: "server1"}, {url: "server2"}];
 *       },
 *       display: (server) => server.url,
 *       getValue: (server) => server.url,
 *       onChange: async (selectedServer, context) => {
 *         // Fetch data needed by child selects
 *         context.datasets = await fetchDatasets(selectedServer);
 *       }
 *     },
 *     "dataset": {
 *       label: "Dataset",
 *       parent: "server",  // Depends on server selection
 *       getter: async (parentValue, context) => {
 *         // Return options based on parent
 *         return context.datasets || [];
 *       },
 *       display: (dataset) => dataset
 *     }
 *   }
 * };
 * 
 * // Render the cascading select
 * const container = d3.select('.properties-cell');
 * const editor = renderCascadingSelectEditor(container, {
 *   reader: 'myReader',
 *   schema: schema,
 *   currentValues: {server: 'server1', dataset: 'dataset1'}, // For editing
 *   idPrefix: 'new',
 *   onError: (error) => console.error(error)
 * });
 * 
 * // Get selected values
 * const values = editor.getValue(); // {server: 'server1', dataset: 'dataset1'}
 * ```
 * 
 * KEY FEATURES:
 * - Properly initializes with existing values when editing
 * - Automatically clears dependent fields when parent changes
 * - Uses MobX observables for reactive updates
 * - Supports async option loading
 * - Prevents race conditions during initialization
 * 
 * SCHEMA FIELD CONFIG:
 * - label: Display label for the field
 * - getter: async (parentValue, context) => options[] - Fetch options
 * - parent: Name of parent field (omit for root fields)
 * - display: (option) => string - How to display option
 * - getValue: (option) => value - Extract value from option (defaults to display)
 * - onChange: (selectedOption, context) => void - Called when selection changes
 */

const { observable, autorun, runInAction } = mobx;

/**
 * Render a cascading select editor where options in child selects depend on parent selections
 * 
 * @param {Object} container - D3 selection for the container element
 * @param {Object} config - Configuration object
 * @param {string} config.reader - The reader type (e.g., 'ddfbw')
 * @param {Object} config.schema - Schema defining the cascade structure
 * @param {Object} config.currentValues - Current values to populate (for editing)
 * @param {string} config.idPrefix - Prefix for input IDs (e.g., 'new' or 'edit')
 * @param {Function} config.onError - Error callback
 * @returns {Object} Object with getValue() method to retrieve selected values
 */
export function renderCascadingSelectEditor(container, config) {
  const { reader, schema, currentValues = {}, idPrefix, onError } = config;
  
  if (!schema || !schema[reader]) {
    console.error(`No schema found for reader: ${reader}`);
    return { getValue: () => ({}) };
  }
  
  const readerSchema = schema[reader];
  const fields = Object.keys(readerSchema);
  
  // Create observable state for selections and context
  const state = observable({
    selections: {},
    context: {},
    isInitialized: false
  });
  
  // Initialize selections with current values
  fields.forEach(fieldName => {
    state.selections[fieldName] = currentValues[fieldName] || null;
  });
  
  // Render each field
  fields.forEach((fieldName, index) => {
    const fieldConfig = readerSchema[fieldName];
    const fieldId = `${idPrefix}-${fieldName}`;
    
    const fieldWrapper = container.append('div')
      .attr('class', 'cascading-field')
      .style('margin-bottom', '8px');
    
    fieldWrapper.append('label')
      .attr('for', fieldId)
      .style('display', 'block')
      .style('margin-bottom', '4px')
      .style('font-weight', '500')
      .text(fieldConfig.label || fieldName);
    
    const selectElement = fieldWrapper.append('select')
      .attr('id', fieldId)
      .attr('class', 'input cascading-select')
      .attr('data-field', fieldName);
    
    // Handle selection changes
    selectElement.on('change', function() {
      const selectedValue = this.value;
      const selectedOption = this.selectedOptions[0]?.__data__;
      
      runInAction(() => {
        state.selections[fieldName] = selectedValue || null;
        
        // Clear dependent fields
        const fieldIndex = fields.indexOf(fieldName);
        for (let i = fieldIndex + 1; i < fields.length; i++) {
          state.selections[fields[i]] = null;
        }
        
        // Call onChange callback if provided
        if (fieldConfig.onChange) {
          fieldConfig.onChange(selectedOption, state.context);
        }
      });
    });
    
    // Setup autorun to populate options
    autorun(async () => {
      const parentField = fieldConfig.parent;
      const parentValue = parentField ? state.selections[parentField] : null;
      
      // Clear options if parent is not selected
      if (parentField && !parentValue) {
        selectElement.selectAll('option').remove();
        selectElement.append('option')
          .attr('value', '')
          .text(`Select ${parentField} first...`);
        return;
      }
      
      try {
        // Fetch options
        const options = await fieldConfig.getter(parentValue, state.context);
        
        if (!options || options.length === 0) {
          selectElement.selectAll('option').remove();
          selectElement.append('option')
            .attr('value', '')
            .text('No options available');
          return;
        }
        
        const displayFn = fieldConfig.display || (d => d);
        const valueFn = fieldConfig.getValue || displayFn;
        
        // Store current selection before updating options
        const currentSelection = state.selections[fieldName];
        
        // Update options
        selectElement.selectAll('option')
          .data([null, ...options])
          .join('option')
          .attr('value', (d, i) => i === 0 ? '' : valueFn(d))
          .text((d, i) => i === 0 ? '-- Select --' : displayFn(d));
        
        // Restore selection if it exists in options
        if (currentSelection) {
          const optionExists = options.some(opt => valueFn(opt) === currentSelection);
          if (optionExists) {
            selectElement.property('value', currentSelection);
            
            // Trigger onChange if not already initialized
            if (!state.isInitialized) {
              const selectedOption = options.find(opt => valueFn(opt) === currentSelection);
              if (fieldConfig.onChange && selectedOption) {
                fieldConfig.onChange(selectedOption, state.context);
              }
            }
          } else {
            // Value not in options, clear it
            runInAction(() => {
              state.selections[fieldName] = null;
            });
          }
        }
        
      } catch (error) {
        console.error(`Error fetching options for ${fieldName}:`, error);
        if (onError) onError(error);
        selectElement.selectAll('option').remove();
        selectElement.append('option')
          .attr('value', '')
          .text('Error loading options');
      }
    });
  });
  
  // Mark as initialized after first render cycle
  setTimeout(() => {
    runInAction(() => {
      state.isInitialized = true;
    });
  }, 100);
  
  // Return interface to get current values
  return {
    getValue: () => {
      const values = {};
      fields.forEach(fieldName => {
        const value = state.selections[fieldName];
        if (value) {
          values[fieldName] = value;
        }
      });
      return values;
    },
    getState: () => state
  };
}
